/**
 * Admission (IPD / EPIC C) shared constants — single source of truth for the
 * admit flow + Admission Desk. Backend enums (`app/schemas/admission.py`,
 * `admission_state.py`) are authoritative; these MIRROR them verbatim so the FE
 * never inlines a role/status/enum literal (memory rule: shared constants only).
 *
 * If the backend enum set changes, update HERE — every admission surface reads
 * these, so the categories can't drift.
 */

import { STAFF_ROLES, STAFF_STATUS } from './staff';

// ── Admission source (POST /admissions `source`) ─────────────────────────
export const ADMISSION_SOURCES = [
  { value: 'planned', label: 'Planned (elective)' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'opd_spawned', label: 'From OPD visit' },
  { value: 'referral_in', label: 'Referral in' },
];
export const ADMISSION_SOURCE = {
  PLANNED: 'planned',
  EMERGENCY: 'emergency',
  OPD_SPAWNED: 'opd_spawned',
  REFERRAL_IN: 'referral_in',
};
/** Emergency relaxes required fields (bed/doctor/diagnosis optional; MLC shown). */
export const isEmergencySource = (source) => source === ADMISSION_SOURCE.EMERGENCY;
/** Planned-interventions (S9) + estimate diagnosis/bed rules apply to ELECTIVE only. */
export const isElectiveSource = (source) => source === ADMISSION_SOURCE.PLANNED;

// ── Payer type (capture-only in C1 — drives no billing logic) ────────────
/**
 * The four payer classes, mirroring the backend `PayerType`. Named here so no
 * surface has to spell a payer literal inline (FIX-14 — the insurance card's
 * leaving-Cashless guard reads `PAYER_TYPE.CASH`, never `'CASH'`).
 * The `payer_type` ⇄ `payer_mode` LINKAGE itself is SERVER-OWNED (build-plan
 * §C8): no mapping table lives on this side, the FE reads the derived value
 * off the PATCH response.
 */
export const PAYER_TYPE = {
  CASH: 'CASH',
  TPA: 'TPA',
  GOVT: 'GOVT',
  CREDIT: 'CREDIT',
};
export const PAYER_TYPES = [
  { value: PAYER_TYPE.CASH, label: 'Cash / self-pay' },
  { value: PAYER_TYPE.TPA, label: 'TPA / insurance' },
  { value: PAYER_TYPE.GOVT, label: 'Govt scheme' },
  { value: PAYER_TYPE.CREDIT, label: 'Credit / corporate' },
];
export const PAYER_TYPE_DEFAULT = PAYER_TYPE.CASH;
export const PAYER_TYPE_LABELS = PAYER_TYPES.reduce(
  (acc, p) => ({ ...acc, [p.value]: p.label }),
  {},
);

/**
 * Payer mode (Wave-3 insurance capture) — the money-spine SETTLEMENT-MECHANISM
 * driver, ORTHOGONAL to `payer_type` above (tech-lead D1 ruling: kept BOTH,
 * never nested/duplicated — `payer_type` answers "who is the payer class"
 * [capture-only MIS], `payer_mode` answers "how does settlement happen").
 * `NONE` = self-pay/cash, no insurer settlement row. `CASHLESS` = insurer
 * settles an approved amount directly, minted once at discharge-initiation.
 * `REIMBURSEMENT` = patient pays the full bill and claims from the insurer
 * separately — no settlement row, claim info only. Mirrors backend `PayerMode`
 * (`admission.py`) verbatim.
 */
export const PAYER_MODE = {
  NONE: 'NONE',
  CASHLESS: 'CASHLESS',
  REIMBURSEMENT: 'REIMBURSEMENT',
};
export const PAYER_MODE_DEFAULT = PAYER_MODE.NONE;
/** Claim-mode 3-way selector options (mock1). COPY: pending Business ratification. */
export const PAYER_MODE_OPTIONS = [
  { value: PAYER_MODE.NONE, label: 'Cash / self-pay', sub: 'No insurer involved. Patient pays the invoice directly.' },
  { value: PAYER_MODE.CASHLESS, label: 'Cashless (TPA approved)', sub: 'Insurer settles an approved amount directly. Reduces what the patient owes.' },
  { value: PAYER_MODE.REIMBURSEMENT, label: 'Reimbursement', sub: 'Patient pays in full, then claims from the insurer using recorded claim info.' },
];
export const PAYER_MODE_LABELS = PAYER_MODE_OPTIONS.reduce(
  (acc, m) => ({ ...acc, [m.value]: m.label }),
  {},
);

/**
 * Admission event kinds this FE branches on for a non-generic Timeline
 * rendering. `DEATH_UPDATED` (death-record slice, invariant 5) is the ONE
 * kind the FE needs to know by name — it carries `refs.changes` (the SAME
 * `[{field, old, new}]` shape `INSURANCE_UPDATED` already uses, S7) so an
 * edit's diff can render under it. `ADMISSION_DEATH_RECORDED` /
 * `ADMISSION_DEATH_REMOVED` need no FE branch — their titles are server-side
 * and the timeline renders `e.title` straight off the row (S6 — no FE
 * event-kind→label map).
 */
export const ADMISSION_EVENT_KIND = {
  INSURANCE_UPDATED: 'ADMISSION_INSURANCE_UPDATED',
  DEATH_UPDATED: 'ADMISSION_DEATH_UPDATED',
  // DOD rule v3 / C3 — the EXISTING details-edit event kind (no new kind was
  // minted); a confirmed-date edit rides it with additive `refs.dod_edit`
  // `{from, to, reason}`, which the Details tab's change history reads.
  DETAILS_UPDATED: 'ADMISSION_DETAILS_UPDATED',
  // Initiate (with a backdating reason) and Cancel initiation ride this kind;
  // `refs.dod_edit {from, to, reason}` carries the audit.
  STATUS_CHANGED: 'ADMISSION_STATUS_CHANGED',
};

// ── BEDSTATE status (§3 BEDSTATE contract — shared BedState const) ────────
// Absent BEDSTATE row ≡ VACANT (sparse). C1 writes OCCUPIED + VACANT only.
export const BED_STATES = {
  VACANT: 'VACANT',
  OCCUPIED: 'OCCUPIED',
  RESERVED: 'RESERVED',
  CLEANING: 'CLEANING',
  BLOCKED: 'BLOCKED',
};
/** Only VACANT (incl. absent-row ≡ VACANT) beds are selectable in the picker. */
export const isBedSelectable = (status) =>
  !status || status === BED_STATES.VACANT;
export const BED_STATE_LABELS = {
  VACANT: 'Vacant',
  OCCUPIED: 'Occupied',
  RESERVED: 'Reserved',
  CLEANING: 'Cleaning',
  BLOCKED: 'Blocked',
};

// ── Admission status (mirror of AdmissionStatus / admission_state.py) ─────
export const ADMISSION_STATUS = {
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  BED_ASSIGNED: 'BED_ASSIGNED',
  ADMITTED: 'ADMITTED',
  IN_TREATMENT: 'IN_TREATMENT',
  DISCHARGE_INITIATED: 'DISCHARGE_INITIATED',
  BILLING_CLEARANCE: 'BILLING_CLEARANCE',
  DISCHARGED: 'DISCHARGED',
  TRANSFERRED_OUT: 'TRANSFERRED_OUT',
  DECEASED: 'DECEASED',
  CANCELLED: 'CANCELLED',
};

export const ADMISSION_STATUS_LABELS = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  BED_ASSIGNED: 'Bed assigned',
  ADMITTED: 'Admitted',
  IN_TREATMENT: 'In treatment',
  DISCHARGE_INITIATED: 'Discharge initiated',
  BILLING_CLEARANCE: 'Billing clearance',
  DISCHARGED: 'Discharged',
  TRANSFERRED_OUT: 'Transferred out',
  DECEASED: 'Deceased',
  CANCELLED: 'Cancelled',
};

/**
 * Desk BUCKET membership (bk-043 — owner-finalized scheme, 2026-07-15). The
 * reception desk asks exactly two questions: "is the patient here?" vs "have
 * they left?" — ONE exported source of truth for bucket→status membership so
 * chips, union loads, counts, and search fallback can never disagree (the
 * owner incident: a BILLING_CLEARANCE patient invisible under "Admitted").
 *
 * CURRENTLY_ADMITTED_STATUSES = every status where the patient is physically
 * still in a bed, INCLUDING mid-discharge (DISCHARGE_INITIATED/
 * BILLING_CLEARANCE) — a mid-discharge patient is NEVER hidden even if nobody
 * clicks the "Discharge in progress" sub-chip (business refinement #1).
 * DISCHARGED_BUCKET_STATUSES = statuses only; LAMA/DAMA/REFERRED/NORMAL are
 * DISCHARGE_TYPE (see below), not statuses, so they ride DISCHARGED as a badge.
 *
 * UNBUCKETED_STATUSES is an EXPLICIT (not derived) list of statuses that are
 * intentionally reachable via More… only — the dead REQUESTED/APPROVED/
 * BED_ASSIGNED ladder + the hygiene CANCELLED status. Being explicit (rather
 * than "every status not otherwise claimed") is what makes the drift test
 * below actually catch a new enum value that nobody homed anywhere.
 */
export const CURRENTLY_ADMITTED_STATUSES = [
  ADMISSION_STATUS.ADMITTED,
  ADMISSION_STATUS.IN_TREATMENT,
  ADMISSION_STATUS.DISCHARGE_INITIATED,
  ADMISSION_STATUS.BILLING_CLEARANCE,
];
export const DISCHARGED_BUCKET_STATUSES = [
  ADMISSION_STATUS.DISCHARGED,
  ADMISSION_STATUS.TRANSFERRED_OUT,
  ADMISSION_STATUS.DECEASED,
];
export const UNBUCKETED_STATUSES = [
  ADMISSION_STATUS.REQUESTED,
  ADMISSION_STATUS.APPROVED,
  ADMISSION_STATUS.BED_ASSIGNED,
  ADMISSION_STATUS.CANCELLED,
];
export const isCurrentlyAdmitted = (status) => CURRENTLY_ADMITTED_STATUSES.includes(status);
export const isDischargedBucket = (status) => DISCHARGED_BUCKET_STATUSES.includes(status);

/**
 * S4 Plan Discharge (`planned_discharge_at`, C2 frozen contract) — the "Plan
 * discharge" button/dialog is reachable at ANY pre-completion status (stories
 * AC3: "editable and clearable at any pre-completion status; locked after
 * completion") — exactly the set the desk already calls "currently admitted"
 * (ADMITTED / IN_TREATMENT / DISCHARGE_INITIATED / BILLING_CLEARANCE).
 * Delegates to `isCurrentlyAdmitted` (single source) rather than a second
 * literal status list that could drift from it.
 */
export const canPlanDischarge = (status) => isCurrentlyAdmitted(status);

/** C2 frozen contract — a plan may be up to this many days in the future (owner ruling 1). */
export const PLAN_DISCHARGE_MAX_AHEAD_DAYS = 7;

/**
 * FIX-1 (R12.1, compliance report) — mirrors the backend's
 * `discharge_service.PLANNED_DISCHARGE_MAX_BACKDATE_DAYS`. A `discharge/complete`
 * stamp more than this many days behind `now` is refused for EVERY role,
 * including ADMIN (unlike the >48h admin-only guard below, this ceiling is
 * role-blind). Read by `dischargeModel.buildDischargeGate`'s completion-gate
 * mirror, never a re-typed `30` literal.
 */
export const PLAN_DISCHARGE_MAX_BACKDATE_DAYS = 30;
// discharge-final: desk (non-admin) may backdate the Initiate date this many IST days;
// the server allows a 5-minute clock-skew grace on "not in the future".
export const DISCHARGE_DATE_DESK_MAX_BACK_DAYS = 7;
export const DISCHARGE_DATE_FUTURE_GRACE_MS = 5 * 60 * 1000;

/**
 * DOD rule v3 (TPA-PREAUTH/dod-at-initiate, build-plan §STORAGE) — the ONE
 * predicate "is this admission's discharge date a CONFIRMED decision, or is
 * it still a plan". The confirmed date REUSES `planned_discharge_at`; its
 * meaning is carried by the `discharge_date_confirmed_at` marker (the instant
 * the confirm happened, NOT the date itself). Mirrors the backend's
 * `admission_state.dod_is_confirmed` VERBATIM — every chip, label, dialog and
 * client-side validator reads THIS, never a second inline check.
 *
 * Absent marker on a live DISCHARGE_INITIATED row (pre-release admissions —
 * ruling 4, read-fallback only, NO backfill) ⇒ false ⇒ reads exactly as it
 * does today (plan → blank).
 */
export const dodIsConfirmed = (admission) =>
  !!(admission && (admission.discharged_at || admission.discharge_date_confirmed_at));

/**
 * Machine error codes `PATCH /admissions/{id}` raises for the `planned_discharge_at`
 * setter (C2 frozen contract). `PlanDischargeDialog`/`planDischargeModel` branch on
 * THESE, never a free-hand string comparison against `error.message`. The two
 * validation codes are pre-empted client-side by `validatePlanDischarge` (same
 * predicate as the server) — reachable here only on a stale-state race.
 */
export const PLAN_DISCHARGE_ERROR_CODE = {
  INVALID_DATETIME: 'INVALID_DATETIME',
  PLANNED_DISCHARGE_BEFORE_ADMISSION: 'PLANNED_DISCHARGE_BEFORE_ADMISSION',
  PLANNED_DISCHARGE_TOO_FAR_AHEAD: 'PLANNED_DISCHARGE_TOO_FAR_AHEAD',
  FIELD_NOT_PERMITTED: 'FIELD_NOT_PERMITTED',
  NO_FIELDS: 'NO_FIELDS',
  ADMISSION_TERMINAL: 'ADMISSION_TERMINAL',
  IPD_TABLE_UNAVAILABLE: 'IPD_TABLE_UNAVAILABLE',
};

/** Bucket key sentinels (URL/filter-state token; never a free-hand string literal). */
export const DESK_BUCKET = {
  CURRENTLY_ADMITTED: 'CURRENTLY_ADMITTED',
  DISCHARGED: 'DISCHARGED',
};
/** Ratified bucket chip labels (rule-consistent — "organisation", not "hospital"). */
export const DESK_BUCKET_LABELS = {
  CURRENTLY_ADMITTED: 'Currently admitted',
  DISCHARGED: 'Discharged',
};
export const DESK_BUCKET_DEFAULT = DESK_BUCKET.CURRENTLY_ADMITTED;

/** Sub-chip key sentinels — narrow the Currently-admitted union CLIENT-SIDE. */
export const DESK_SUBCHIP = {
  AWAITING_BED: 'AWAITING_BED',
  BED_ASSIGNED: 'BED_ASSIGNED',
  DISCHARGE_IN_PROGRESS: 'DISCHARGE_IN_PROGRESS',
  NO_DOCTOR: 'NO_DOCTOR',
};
export const DESK_SUBCHIP_LABELS = {
  AWAITING_BED: 'Awaiting bed',
  BED_ASSIGNED: 'Bed assigned',
  DISCHARGE_IN_PROGRESS: 'Discharge in progress',
  NO_DOCTOR: 'No doctor',
};

/**
 * bucketForStatus — which bucket (if any) a RAW status belongs to. Returns
 * null for an UNBUCKETED status (More…-only; e.g. REQUESTED, CANCELLED).
 * Single source consumed by the deep-link codec + post-admit/suggestion-pick
 * "which view do I land on" logic — never a second inline mapping.
 */
export function bucketForStatus(status) {
  if (isCurrentlyAdmitted(status)) return DESK_BUCKET.CURRENTLY_ADMITTED;
  if (isDischargedBucket(status)) return DESK_BUCKET.DISCHARGED;
  return null;
}

/**
 * More… dropdown (S2 AC4) = every raw status, incl. Cancelled (demoted from
 * primary) — DERIVED from the enum so it can never miss a newly-added status.
 */
export const ADMISSION_STATUS_FILTERS_MORE = Object.values(ADMISSION_STATUS);
export const ADMISSION_STATUS_FILTER_DEFAULT = ADMISSION_STATUS.ADMITTED;

/**
 * daysAdmitted — length of stay in WHOLE days since `admitted_at` (0 = admitted
 * today). Returns null when the row has no admitted_at (pre-admission statuses).
 * Single source consumed by the desk row LOS badge, the detail panel and the CSV
 * export so the three surfaces cannot diverge.
 */
export function daysAdmitted(admission, now = new Date()) {
  const iso = admission && admission.admitted_at;
  if (!iso) return null;
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return null;
  const ms = now.getTime() - start.getTime();
  if (ms < 0) return 0;
  return Math.floor(ms / 86400000);
}

/**
 * statusSince — timestamp (ISO) the admission entered its CURRENT status: the
 * last `status_history` entry's `at`, falling back to `updated_at` when history
 * is empty/absent. Returns null if neither is present.
 */
export function statusSince(admission) {
  if (!admission) return null;
  const hist = admission.status_history;
  if (Array.isArray(hist) && hist.length > 0) {
    const last = hist[hist.length - 1];
    if (last && last.at) return last.at;
  }
  return admission.updated_at || null;
}

/**
 * Cancel/void allowed-set — DERIVED from the frozen state machine
 * (§2.6: CANCELLED ∈ TRANSITIONS[from]). No inline status literals scattered
 * per surface; the desk's "Cancel/Void" action reads THIS set only.
 * REQUESTED/APPROVED/BED_ASSIGNED cancel + the owner-gate ADMITTED→CANCELLED VOID.
 */
export const CANCELABLE_STATUSES = new Set([
  ADMISSION_STATUS.REQUESTED,
  ADMISSION_STATUS.APPROVED,
  ADMISSION_STATUS.BED_ASSIGNED,
  ADMISSION_STATUS.ADMITTED,
]);
export const isCancelable = (status) => CANCELABLE_STATUSES.has(status);

/**
 * Cancel money-guard (interim FRONTEND guard rail, owner-approved — the real
 * backend cancel gate is parked). Cancelling an admission that still holds
 * money strands it: once CANCELLED, both refund paths (`refund_deposit` /
 * `return_advance`) reject the admission (neither in the settlement window
 * nor a locked correction), so that money becomes unreturnable through the
 * product. Reads ONLY fields already on the admission row — no new request.
 *
 * `deposit_total_paise` is NET (deposits minus refunds already applied), so a
 * fully-refunded deposit reads 0 and unblocks on its own — never gate on
 * "was a deposit ever collected". Net charges are DERIVED the same way, never
 * a second stored/re-fetched figure: `balance_paise + deposit_total_paise`.
 * For a still-admitted row (the only status this guard ever sees — dues
 * collection and insurance settlement only exist post-discharge) that is
 * exact. Returns null when cancel is unblocked; otherwise the reason the
 * disabled control shows. Deposit leads when both are true.
 */
export function cancelBlockedReason(admission) {
  const a = admission || {};
  // death-record slice (invariant 4/S1) — `transition -> CANCELLED` refuses
  // `409 DEATH_RECORDED_TYPE_LOCKED` while a death record exists. Mirrored
  // HERE (the ONE cancel-guard source) so the "Cancel admission" menu item
  // states WHY, never silently disabled (D40) — checked first, ahead of the
  // money guard below.
  if (hasOpenDeathRecord(a)) return 'A death is recorded on this admission. Remove it on Manage Admission before cancelling.';
  const depositPaise = Number(a.deposit_total_paise) || 0;
  const netChargesPaise = (Number(a.balance_paise) || 0) + depositPaise;
  if (depositPaise > 0) return 'A deposit is held on this admission. Refund the deposit before cancelling.';
  if (netChargesPaise > 0) return 'Charges are posted on this admission. Reverse the charges before cancelling.';
  return null;
}

/**
 * Admission-level session permission slugs (backend `role_permissions.py` is
 * the source of truth; mirrored here so the FE never inlines a literal).
 * TRANSFER_BED (day-1 ADDENDUM-2/S7) = move a bedded admission to a new bed —
 * receptionist/billing/admin. Distinct from `attachBed` (assign a bed to a
 * bed-less admission), which needs no permission slug of its own today.
 * ORDER_ADMISSION_LAB (day-1 ORCHESTRATOR ADDENDUM-4/S8-THIN) = the
 * admission-side "Order lab" entry point — existing slug (`ADMISSION_PERMISSION_SLUGS`
 * in `role_permissions.py`), granted to RECEPTIONIST+BILLING on a parallel BE task.
 */
export const ADMISSION_PERMISSIONS = {
  // admission-access S1 — the Manage Admission route gate (existing backend
  // slug; ADMIN inherits via FULL_ACCESS). Mirrored here so the page gate never
  // inlines the literal.
  VIEW_ADMISSION: 'VIEW_ADMISSION',
  // Desk toolbar "Occupancy board" link — same slug the board route/page gates on.
  VIEW_OCCUPANCY_BOARD: 'VIEW_OCCUPANCY_BOARD',
  TRANSFER_BED: 'TRANSFER_BED',
  ORDER_ADMISSION_LAB: 'ORDER_ADMISSION_LAB',
  // M2 (diagnostics card) — existing slug (`organisation_lab.py:290`), gates
  // the per-row Cancel action; no backend change needed.
  CANCEL_LAB_ORDER: 'CANCEL_LAB_ORDER',
  // Wave-3 insurance capture (C-1 frozen contract) — the PATCH the Payer &
  // insurance card writes through rides the EXISTING admission-capture perm
  // (same slug the backend's PATCH /admissions/{id} route gates on).
  ADMIT_PATIENT: 'ADMIT_PATIENT',
  // bk-1710 — the slug POST /admissions/{id}/transition requires for
  // to_status=CANCELLED (backend TRANSITION_PERMISSION_BY_TARGET); gates the
  // "Cancel admission" action so it is never offered to a role the server refuses.
  CANCEL_ADMISSION: 'CANCEL_ADMISSION',
  // C7 (diag-gst D27) — gates the IPD tile/page, granted to RECEPTIONIST by
  // role default (ADMIN inherits via FULL_ACCESS). Reaches the admitted-
  // patient picker view AND widens the order/cancel/lab-bill gates via
  // require_any_permission — a staff member without VIEW_ADMISSION still
  // reaches the money-free IPD Diagnostics view.
  ACCESS_IPD: 'ACCESS_IPD',
  // ADMISSION-ACCESS ADD-1 (g247) — the clinical-record view slug (DOCTOR /
  // NURSE / RECEPTIONIST; ADMIN via FULL_ACCESS). The IPD tile + page gate is
  // `ACCESS_IPD OR VIEW_CLINICAL_RECORD`; ACCESS_IPD itself is NOT widened.
  VIEW_CLINICAL_RECORD: 'VIEW_CLINICAL_RECORD',
  // S4 Plan Discharge (C2 owner ruling 4) — the OR-gate setter perm; SAME
  // string/slug as `DISCHARGE_PERMISSIONS.DISCHARGE_PATIENT` below (mirrored
  // here, not duplicated meaning: DOCTOR reaches "Plan discharge" via this
  // slug, without needing ADMIT_PATIENT). Never a second backend slug.
  DISCHARGE_PATIENT: 'DISCHARGE_PATIENT',
};

/**
 * S12 "Today's IPD report" + IPD dashboard (RBAC-IPD / FE-1) — the report and
 * dashboard routes are PERMISSION-gated server-side via
 * `require_permission(VIEW_IPD_REVENUE / VIEW_IPD_DASHBOARD)` (backend
 * `role_permissions.py` is the source of truth; mirrored here so the FE
 * never inlines a literal). ADMIN holds both via FULL_ACCESS; RECEPTIONIST
 * (IPD-on org) can be granted both by default.
 */
export const REPORT_PERMISSIONS = {
  VIEW_IPD_REVENUE: 'VIEW_IPD_REVENUE',
  VIEW_IPD_DASHBOARD: 'VIEW_IPD_DASHBOARD',
};

/**
 * Machine error codes the transfer-bed endpoint raises (ADDENDUM-2 frozen
 * contract). Dialogs branch on THESE, never a free-hand string comparison.
 */
export const TRANSFER_ERROR_CODE = {
  ADMISSION_NOT_FOUND: 'ADMISSION_NOT_FOUND',
  NO_CURRENT_BED: 'NO_CURRENT_BED',
  SAME_BED: 'SAME_BED',
  BED_NOT_AVAILABLE: 'BED_NOT_AVAILABLE',
  BED_OCCUPIED: 'BED_OCCUPIED',
  TRANSFER_CONFLICT: 'TRANSFER_CONFLICT',
  ADMISSION_NOT_ACTIVE: 'ADMISSION_NOT_ACTIVE',
};

/**
 * "Awaiting bed" — a DERIVED indicator (S10), never a stored flag: an admission
 * that is ADMITTED with no bed attached. SINGLE SOURCE OF TRUTH consumed by the
 * desk chip/filter AND the occupancy-board badge, so the two surfaces cannot
 * drift. Emergency and elective no-bed rows resolve identically here.
 */
export const isAwaitingBed = (admission) =>
  !!admission
  && admission.status === ADMISSION_STATUS.ADMITTED
  && !admission.current_bed_id;

/**
 * "Bed assigned" — the DERIVED complement of isAwaitingBed: an admission that is
 * ADMITTED with a bed attached. Same single-source rationale — the born-ADMITTED
 * flow never populates the RAW BED_ASSIGNED ladder status, so the desk's
 * "Bed assigned" chip counts THIS derived subset of the ADMITTED partition, never
 * a raw-status partition. Consumed by the desk chip/filter; any other surface that
 * needs the same split reads this so the two can't drift.
 */
export const isBedAssigned = (admission) =>
  !!admission
  && admission.status === ADMISSION_STATUS.ADMITTED
  && admission.current_bed_id != null;

/**
 * "No doctor" — a DERIVED indicator, never a stored flag: a CURRENTLY-ADMITTED
 * admission with no primary doctor assigned. Reads `primary_doctor_member_id`
 * — the SAME field the desk's doctor-dropdown filter (`applyDeskFilters`)
 * already compares against, so "assigned"/"unassigned" can't disagree between
 * the two filters. Gated on `isCurrentlyAdmitted` (single source, same as
 * `bucketForStatus`) rather than the ADMITTED-only gate `isAwaitingBed`/
 * `isBedAssigned` use, because the sub-chip narrows the FULL Currently-admitted
 * union (same span as "Low deposit", Q3 owner decision) — a discharged row
 * missing a doctor is a historical record, not a work-queue item.
 */
export const isNoDoctor = (admission) =>
  !!admission
  && isCurrentlyAdmitted(admission.status)
  && !admission.primary_doctor_member_id;

/** Terminal statuses (no further transitions; hide row actions). */
export const TERMINAL_STATUSES = new Set([
  ADMISSION_STATUS.DISCHARGED,
  ADMISSION_STATUS.TRANSFERRED_OUT,
  ADMISSION_STATUS.DECEASED,
  ADMISSION_STATUS.CANCELLED,
]);
export const isTerminal = (status) => TERMINAL_STATUSES.has(status);

/**
 * C3b — mirrors the backend's `admission_state.INVOICE_BEARING_STATUSES`
 * (`app/services/admission_state.py`) verbatim: "from discharge-initiation
 * onward", the owner's rule word for word. An invoice number is minted at
 * DISCHARGE_INITIATED, so a lab order on a stay in any of these statuses can
 * no longer be cancelled — the SERVER enforces that (`lab_service.cancel`,
 * C3a); this list only lets the Diagnostics card MIRROR the guard (hide the
 * button) so staff don't click into a refusal. Never re-derive this set
 * inline, never an `=== 'DISCHARGE_INITIATED'` literal.
 */
export const INVOICE_BEARING_STATUSES = new Set([
  ADMISSION_STATUS.DISCHARGE_INITIATED,
  ADMISSION_STATUS.BILLING_CLEARANCE,
  ADMISSION_STATUS.DISCHARGED,
  ADMISSION_STATUS.DECEASED,
]);
export const isInvoiceBearing = (status) => INVOICE_BEARING_STATUSES.has(status);

// ── Planned-intervention catalog kinds (S9) ──────────────────────────────
export const INTERVENTION_TYPES = {
  SURGERY: 'surgery',
  PROCEDURE: 'procedure',
};

// ── Charge ledger (C3 — mirror of ChargeResponse.kind, §2 contract) ───────
// A charge is one of five immutable kinds. BED_DAY is auto-posted by the
// midnight accrual job; MANUAL is the legacy admin-only free-text ad-hoc line
// (day-1 S2 AC4 — gated on MANAGE_CHARGE_MASTER); ITEM (day-1 S2, additive) is
// a server-priced line posted from the Charge Items master via the desk
// picker — no rate ever typed; REVERSAL is a negative correction line linked
// to an original charge (POSTED is immutable — corrections are reversals,
// never edits); CONCESSION (C4 defect fix, frozen contract) is an
// admin-granted bill-side waiver — a negative credit line capped at net
// POSTED charges, distinct from a REFUND (which returns a deposit already
// collected — deposits are untouched by a waiver).
export const CHARGE_KIND = {
  BED_DAY: 'BED_DAY',
  MANUAL: 'MANUAL',
  ITEM: 'ITEM',
  REVERSAL: 'REVERSAL',
  CONCESSION: 'CONCESSION',
};
export const CHARGE_KIND_LABELS = {
  BED_DAY: 'Bed day',
  MANUAL: 'Other charge',
  ITEM: 'Charge item',
  REVERSAL: 'Reversal',
  CONCESSION: 'Waiver / concession',
};

/**
 * Charge-item master `category` (§C2 frozen contract, bed-charging build
 * plan). ROOM_BED is the only category value FE branches on today — the
 * seeded bed-class items (BE-4) carry it so the Add-Charge picker can show
 * the frozen-tariff suggested-rate hint + the bed-day helper text without a
 * name/string match. Never inline the `'ROOM_BED'` literal.
 */
export const CHARGE_ITEM_CATEGORY = {
  ROOM_BED: 'ROOM_BED',
};
export const isBedDayChargeItem = (item) =>
  !!item && item.category === CHARGE_ITEM_CATEGORY.ROOM_BED;

/**
 * Machine error codes the charge-ledger endpoints raise (day-1 §2.2/§2.5
 * frozen contract). Dialogs branch on THESE, never a free-hand string
 * comparison against `error.message`.
 *
 * PRICE_OUT_OF_RANGE (OPD-CHARGE-RANGE §1.5 frozen contract) — the post-time
 * fence on `POST .../item-charges` for a variable-price item: amount outside
 * the item's current range, missing on a variable item, or sent on a fixed
 * item. Detail carries `min_price_paise`/`max_price_paise`/`submitted_paise`
 * (int|null, PAISE) + `variable_price` (the item's current server flag).
 */
export const CHARGE_ERROR_CODE = {
  ALREADY_REVERSED: 'ALREADY_REVERSED',
  REVERSAL_WINDOW_ELAPSED: 'REVERSAL_WINDOW_ELAPSED',
  CHARGE_ITEM_NOT_FOUND: 'CHARGE_ITEM_NOT_FOUND',
  CHARGE_ITEM_INACTIVE: 'CHARGE_ITEM_INACTIVE',
  PRICE_OUT_OF_RANGE: 'PRICE_OUT_OF_RANGE',
  SERVICE_DATE_AFTER_DISCHARGE: 'SERVICE_DATE_AFTER_DISCHARGE',
};

/** GST rates the manual-charge picker offers (mirror of §2 C-2 gst_rate enum). */
export const GST_RATES = [0, 5, 12, 18];

/**
 * Charge-ledger session permission slugs (backend `role_permissions.py` is the
 * source of truth; these mirror the C3 slugs so the FE never inlines a literal).
 * VIEW_RUNNING_BILL = read; ADD_CHARGE = manual/item line (reception/billing/
 * admin); REVERSE_CHARGE = reverse a line (day-1: receptionist same-day +
 * admin any day, S5); GRANT_CONCESSION = bill-side waiver/concession (C4
 * defect fix, ADMIN only — same tier as REFUND_DEPOSIT, segregation of
 * duties). MANAGE_CHARGE_MASTER (day-1 S1/S2) = maintain the Charge Items
 * master AND reach the legacy free-text one-off charge path — ADMIN only.
 */
export const CHARGE_PERMISSIONS = {
  VIEW_RUNNING_BILL: 'VIEW_RUNNING_BILL',
  ADD_CHARGE: 'ADD_CHARGE',
  REVERSE_CHARGE: 'REVERSE_CHARGE',
  GRANT_CONCESSION: 'GRANT_CONCESSION',
  MANAGE_CHARGE_MASTER: 'MANAGE_CHARGE_MASTER',
};

/**
 * Running-bill ledger filter chips (R10 long-list). Keys are DERIVED from
 * CHARGE_KIND (+ an ALL sentinel) so the chip set can't drift from the kinds.
 * ALL shows everything; the kind chips filter to that kind.
 */
export const CHARGE_FILTER_ALL = 'ALL';
export const CHARGE_FILTER_CHIPS = [
  { key: CHARGE_FILTER_ALL, label: 'All' },
  { key: CHARGE_KIND.BED_DAY, label: 'Bed days' },
  { key: CHARGE_KIND.ITEM, label: 'Charge items' },
  { key: CHARGE_KIND.MANUAL, label: 'Other charges' },
  { key: CHARGE_KIND.REVERSAL, label: 'Reversals' },
  { key: CHARGE_KIND.CONCESSION, label: 'Waivers' },
];

/** Ledger collapses to the latest N day-groups; older days sit behind an expander. */
export const LEDGER_VISIBLE_DAYS = 15;

// ═══════════════════════════════════════════════════════════════════════
// Deposits + live balance (C4 — mirror of the frozen §1 API CONTRACT)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Payment session permission slugs (backend `role_permissions.py` is the source
 * of truth; mirrored here so the FE never inlines a literal). COLLECT_DEPOSIT =
 * take an advance (reception/billing); REFUND_DEPOSIT = free-form refund a
 * deposit — EDITABLE amount + free-text reason (admin/supervisor tier ONLY —
 * segregation of duties); RETURN_ADVANCE = the bounded discharge-time return of
 * the definite pending excess (amount + reason server-set; reception + admin);
 * GENERATE_RECEIPT_PDF = view / print / list receipts + refund vouchers.
 */
export const PAYMENT_PERMISSIONS = {
  COLLECT_DEPOSIT: 'COLLECT_DEPOSIT',
  REFUND_DEPOSIT: 'REFUND_DEPOSIT',
  RETURN_ADVANCE: 'RETURN_ADVANCE',
  GENERATE_RECEIPT_PDF: 'GENERATE_RECEIPT_PDF',
  // COLLECT_PAYMENT (existing org-wide slug, ADMIN/RECEPTIONIST/BILLING) also
  // gates the day-1 lab-credit Settle Payment card mount (§2.8/§2.9, ADD-3
  // L3) — mirrored here so the mount point never inlines the literal.
  COLLECT_PAYMENT: 'COLLECT_PAYMENT',
};

/** PaymentLine.source_type (C-1). A deposit is +, a refund is − on net. */
export const PAYMENT_SOURCE_TYPE = {
  IPD_DEPOSIT: 'IPD_DEPOSIT',
  REFUND: 'REFUND',
};

/** PaymentLine.status (C-1). Derived "fully refunded" reads refundable_remaining, never status. */
export const PAYMENT_STATUS = {
  SETTLED: 'SETTLED',
  REFUNDED: 'REFUNDED',
  VOIDED: 'VOIDED',
};

/**
 * Payment modes the deposit dialog offers — the org's existing OPD modes (no
 * gateway). `reference` = whether a per-mode reference field is revealed/required
 * (driven BY mode, cleared when switching back to cash). Cash carries no reference.
 * Labels for the reference field are mode-specific.
 */
export const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash', reference: false },
  { value: 'UPI', label: 'UPI', reference: true, refLabel: 'UPI reference no.' },
  { value: 'CARD', label: 'Card', reference: true, refLabel: 'Card transaction reference' },
  { value: 'NETBANKING', label: 'Net banking', reference: true, refLabel: 'Transaction reference' },
  { value: 'CHEQUE', label: 'Cheque', reference: true, refLabel: 'Cheque no.' },
];
export const PAYMENT_MODE_DEFAULT = 'CASH';
export const PAYMENT_MODE_LABELS = PAYMENT_MODES.reduce(
  (acc, m) => ({ ...acc, [m.value]: m.label }),
  {},
);
/** Per-mode metadata (reference flag + field label) keyed by mode — single source for any dialog revealing a reference input. */
export const PAYMENT_MODE_META = PAYMENT_MODES.reduce(
  (acc, m) => ({ ...acc, [m.value]: m }),
  {},
);

/**
 * Credit-settle payment modes (lab-credit §2.9 FROZEN contract) — a narrower
 * set than the deposit dialog's `PAYMENT_MODES` (no NETBANKING/CHEQUE; the
 * settle route's `payment_mode` Literal is CASH|CARD|UPI|QR only). `reference`
 * mirrors the server's 422 REFERENCE_REQUIRED rule (required for CARD/UPI/QR).
 */
export const CREDIT_SETTLE_PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash', reference: false },
  { value: 'UPI', label: 'UPI', reference: true, refLabel: 'UPI reference no.' },
  { value: 'CARD', label: 'Card', reference: true, refLabel: 'Card transaction reference' },
  { value: 'QR', label: 'QR', reference: true, refLabel: 'QR transaction reference' },
];

/**
 * `GET /credits?status=` entry `settlement_status` enum (FROZEN §T2 contract,
 * build-plan-labsettle.md) — ONE source so the combined-ledger card never
 * inlines the "OPEN"/"SETTLED" literal. NOTE this is a DIFFERENT enum from the
 * IPD-lab synth row's `settlement_status` ("SETTLED"|"UNSETTLED", ManageVisitPage)
 * — the two surfaces bind to two distinct BE contracts by design, not drift.
 */
export const CREDIT_SETTLEMENT_STATUS = { OPEN: 'OPEN', SETTLED: 'SETTLED' };
export const CREDIT_SETTLEMENT_STATUS_CHIP_LABEL = { OPEN: 'Unsettled', SETTLED: 'Settled' };
/** Default payout mode for a refund / advance return (chips default here). */
export const CASH_MODE = 'CASH';
/**
 * Refund / advance-return payout modes (owner redesign — the fully-free method
 * choice): ANY mode in → ANY of Cash/UPI/Card out; the payout needn't match how
 * the deposit was collected. UPI + Card carry a REQUIRED reference (mirrors the
 * backend's 422 REFERENCE_REQUIRED rule). Cash carries none. NETBANKING/CHEQUE
 * are NOT offered on the payout side (the contract's method Literal is
 * CASH|UPI|CARD only). Single source consumed by both the free-form RefundDialog
 * and the bounded ReturnAdvanceDialog mode chips.
 */
export const REFUND_PAYOUT_MODES = ['CASH', 'UPI', 'CARD'];
export function allowedRefundModes() {
  return REFUND_PAYOUT_MODES;
}
/** Preset reason shown read-only in the bounded return; the SERVER sets the real reason (client sends none). */
export const RETURN_ADVANCE_REASON = 'Excess advance returned to patient at discharge';

/**
 * Deposits/refunds list filter chips (R10 long-list). Keys DERIVED from the two
 * source types (+ an ALL sentinel) so the chip set can't drift.
 */
export const PAYMENTS_FILTER_ALL = 'ALL';
export const PAYMENTS_FILTER_CHIPS = [
  { key: PAYMENTS_FILTER_ALL, label: 'All' },
  { key: PAYMENT_SOURCE_TYPE.IPD_DEPOSIT, label: 'Deposits' },
  { key: PAYMENT_SOURCE_TYPE.REFUND, label: 'Refunds' },
];
/** The deposits/refunds list collapses beyond the latest N rows (owner R10). */
export const PAYMENTS_VISIBLE_ROWS = 6;
/** Long-list search + collapse affordances appear past this many rows. */
export const PAYMENTS_LONG_LIST_THRESHOLD = 20;

/** Server-driven "server prices/validates" max deposit (contract literal) — client hint only. */
export const MAX_DEPOSIT_PAISE = 10000000 * 100;

/**
 * Deposit collectable across the settlement window — ADMITTED (incl. no-bed
 * — business ruling 4: "admit without bed to take an advance"),
 * DISCHARGE_INITIATED, and BILLING_CLEARANCE (bug #7: a stay initiated for
 * discharge with an outstanding balance must still be able to collect it —
 * the backend's `_COLLECT_STATUSES` already allows this window; the FE gate
 * was narrower than the server and produced a discharge-collect deadlock:
 * "Add deposit" blocked while Complete required balance == 0). Delegates to
 * the ONE `SETTLEMENT_WINDOW_STATUSES` set below — never a second inline
 * status list. REQUESTED/APPROVED (pre-admit) and every terminal status
 * remain blocked. Single source consumed by both surfaces' Add-deposit
 * affordance + the dialog guard.
 */
export const isDepositCollectable = (status) => isInSettlementWindow(status);

// ═══════════════════════════════════════════════════════════════════════
// Discharge → Final Bill (C5 — mirror of the frozen API CONTRACT
// `DischargeType` in `app/schemas/admission.py` and the ONE
// `SETTLEMENT_WINDOW_STATUSES` constant in `app/services/admission_state.py`.
// Every discharge surface (FE-2..5) reads THESE, never an inline literal.)
// ═══════════════════════════════════════════════════════════════════════

/** DischargeType enum (contract #3 body `discharge_type`). */
export const DISCHARGE_TYPE = {
  NORMAL: 'NORMAL',
  LAMA: 'LAMA',
  DAMA: 'DAMA',
  DECEASED: 'DECEASED',
  REFERRED: 'REFERRED',
};

/** Patient-facing option labels (copy-deck §1.2 — ratified strings, never free-handed). */
export const DISCHARGE_TYPE_LABELS = {
  NORMAL: 'Routine discharge',
  LAMA: 'Left against medical advice (LAMA)',
  DAMA: 'Discharged against medical advice (DAMA)',
  DECEASED: 'Death',
  REFERRED: 'Referred to another facility',
};

/**
 * Dues-eligible types (owner gate resolution): completing with a non-zero
 * balance is recorded as dues ONLY for these three; NORMAL/REFERRED must
 * settle to ₹0 to complete. Single source for the dues-panel reveal
 * (FE-2) and any client-side mirror of the server completion gate.
 */
export const DUES_ELIGIBLE_DISCHARGE_TYPES = new Set([
  DISCHARGE_TYPE.LAMA,
  DISCHARGE_TYPE.DAMA,
  DISCHARGE_TYPE.DECEASED,
]);
export const isDuesEligibleDischargeType = (type) => DUES_ELIGIBLE_DISCHARGE_TYPES.has(type);

/**
 * bk-752 — every status a death is recordable from (mirrors the backend's
 * table-derived `admission_state.DECEASED_FROM_STATUSES`). The discharge
 * dialog's offered options must match this, never a flat list that offers
 * Death where the server would refuse it.
 */
export const DECEASED_FROM_STATUSES = new Set([
  ADMISSION_STATUS.ADMITTED,
  ADMISSION_STATUS.IN_TREATMENT,
  ADMISSION_STATUS.DISCHARGE_INITIATED,
  ADMISSION_STATUS.BILLING_CLEARANCE,
]);

/**
 * T-F0 (bk-752 narrow slice) — the ONE derived source for "this outcome is
 * a death", accepting either an admission STATUS or a DISCHARGE_TYPE value.
 * `DischargeDialog` (offered options + copy) and the discharge-summary
 * surfaces (T-R2) both import THIS — never a re-derived inline
 * `=== 'DECEASED'` literal.
 */
export const isDeathOutcome = (statusOrType) =>
  statusOrType === ADMISSION_STATUS.DECEASED || statusOrType === DISCHARGE_TYPE.DECEASED;

/**
 * hasOpenDeathRecord — death-record slice (design-model.md S1) — the ONE
 * derived source for "a death is recorded and the stay is still open":
 * `cause_of_death != null` (S1's ONE test for "a death is recorded")
 * combined with a non-terminal status. Every flag surface (banner, desk row,
 * bed tile, discharge type-lock) reads THIS, never a re-derived inline
 * check — the exact "each piece works, together they don't" failure the
 * design model's worked example exists to catch. Distinct from
 * `isDeathOutcome` above, which tests a STATUS/DISCHARGE_TYPE value, not an
 * admission object — recording a death changes neither (invariant 7), so a
 * `DECEASED` status/discharge type is reached only AFTER this flag clears.
 *
 * TWO ROW SHAPES, ONE PREDICATE. The full `AdmissionResponse` carries the
 * `cause_of_death` map; the money-free picker projection behind `/org/ipd`
 * (`AdmissionPickerView`) carries a `death_recorded` boolean instead — that
 * page is stripped at the data level and has no business holding the cause
 * text. Accepting BOTH shapes here is what keeps `/org/ipd` and
 * `/org/admissions` on the same rule: the alternative was an inline
 * `row.death_recorded` check on the IPD page, i.e. a second definition of
 * "is this patient deceased", which is precisely the drift this function
 * exists to prevent. The non-terminal half is NOT duplicated server-side for
 * the same reason — the server sends presence, this decides openness.
 */
export const hasOpenDeathRecord = (admission) =>
  !!admission
  && (admission.cause_of_death != null || admission.death_recorded === true)
  && !isTerminal(admission.status);

/**
 * buildDeclaringDoctorOptions — DOCTOR-only, ACTIVE-only staff roster filter
 * for the death-record dialog's declaring-doctor picker (S3 — the server
 * resolves the display name from this id; the client's typed name text is
 * never trusted, never stored). Lives HERE, not in `dischargeModel.js`'s
 * role-agnostic `buildAuthoriserOptions` (the write-off "Authorised by"
 * picker accepts any active staff member) — the two pickers answer different
 * questions and must not share one filter. The mapping shape is duplicated
 * rather than imported from `dischargeModel.js` to avoid a `constants/` file
 * importing from a `components/` file.
 */
export function buildDeclaringDoctorOptions(staff = []) {
  return (staff || [])
    .filter((m) => m
      && (m.member_id || m.staff_id || m.id)
      && m.status === STAFF_STATUS.ACTIVE
      && m.role === STAFF_ROLES.DOCTOR)
    .map((m) => {
      const memberId = m.member_id || m.staff_id || m.id;
      const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
      return { member_id: memberId, name: name || m.username || memberId };
    });
}

/**
 * Settlement window — the ONE constant (freeze per the build-plan's
 * code-grounding delta: design-model wins over dynamodb.md; IN_TREATMENT is
 * NOT in this window today because no row can reach IN_TREATMENT yet — add
 * it here the day C6 wires that edge, and every consumer below updates in
 * lockstep). Collect / refund / concession / discharge initiate&clear all
 * read THIS set — never three separate lists.
 */
export const SETTLEMENT_WINDOW_STATUSES = new Set([
  ADMISSION_STATUS.ADMITTED,
  ADMISSION_STATUS.DISCHARGE_INITIATED,
  ADMISSION_STATUS.BILLING_CLEARANCE,
]);
export const isInSettlementWindow = (status) => SETTLEMENT_WINDOW_STATUSES.has(status);

/** A discharge can be STARTED from ADMITTED or IN_TREATMENT only (contract #1). */
export const isDischargeStartable = (status) =>
  status === ADMISSION_STATUS.ADMITTED || status === ADMISSION_STATUS.IN_TREATMENT;

/** Row is mid-discharge (resume, not restart) once initiate/clear has fired. */
export const isInDischargeFlow = (status) =>
  status === ADMISSION_STATUS.DISCHARGE_INITIATED || status === ADMISSION_STATUS.BILLING_CLEARANCE;

/**
 * Insurance-amount capture fields (copay/disallowed) are editable only up to
 * discharge-initiation (D4, owner-locked edit model B — the settlement mints
 * once at discharge-initiation from whatever the fields hold at that moment;
 * a post-initiation correction is a deferred residual). Once the admission
 * reaches DISCHARGE_INITIATED/BILLING_CLEARANCE or any terminal status, the
 * capture panel shows the amounts read-only ("Confirmed" instead of
 * "Estimate"). Claim-identity fields (insurer/policy/claim/TPA) and the mode
 * itself are NOT locked by this. `insurer_approved_paise` is no longer part
 * of this lock (TPA-PREAUTH/tpa-bill S2) — it moved to the approval-rounds
 * list below, gated by `canEditApprovalRounds`.
 */
/**
 * DOD rule v3 / S7 (owner addendum 2026-09-19) — REVERSES the D4 lock above:
 * `payer_mode`, `copay_paise` and `disallowed_paise` stay editable through
 * DISCHARGE_INITIATED / BILLING_CLEARANCE (BE-3 empties
 * `_INSURANCE_LOCKED_FIELDS` for those two statuses) so a pre-auth that
 * collapses during the discharge window is fixed at the desk instead of
 * blocking the discharge. TERMINALS stay locked → the server's existing
 * `409 INSURANCE_LOCKED`.
 */
export const canEditInsuranceAmounts = (status) => !isTerminal(status);

/**
 * Approval-rounds edit window (TPA-PREAUTH/tpa-bill S2/S3, C1 contract §Edit
 * window / S3 B-prime) — every non-terminal status. Since S7 widened
 * `canEditInsuranceAmounts` to the identical rule, this is an ALIAS of it
 * (ONE helper, two named consumers: `PayerInsuranceCard` for the mode +
 * amounts, `ApprovalRoundsList` for the rounds) — never a second copy of the
 * predicate that could drift.
 */
export const canEditApprovalRounds = canEditInsuranceAmounts;

/** C1 contract cap on approval rounds per admission (422 `TOO_MANY_ROUNDS`). */
export const APPROVAL_ROUNDS_MAX = 10;

/**
 * Approval-round machine error codes — `PATCH /admissions/{id}/insurance`
 * (C1, TPA-PREAUTH/tpa-bill). Mirrored verbatim so the FE never inlines a
 * code string literal; the wire `message` is never UI copy (contract: "No UI
 * strings on the wire"). `REASON_REQUIRED` is S3's decrease guard —
 * CONTRACT RECONCILE: the build plan drafted it as
 * `DECREASE_REASON_REQUIRED`, the BUILT backend raises `REASON_REQUIRED`
 * (`admission_service.InsuranceReasonRequired`) with `{old_total_paise,
 * new_total_paise}`. The same spelling is used by the UNRELATED
 * cancel-admission guard (`DISCHARGE_ERROR_CODE.REASON_REQUIRED`, a
 * different endpoint) — never key one global message map by code alone;
 * each surface maps the codes of the endpoint it called.
 */
export const APPROVAL_ROUND_ERROR = {
  APPROVED_IS_DERIVED: 'APPROVED_IS_DERIVED',
  EXPECTED_APPROVED_REQUIRED: 'EXPECTED_APPROVED_REQUIRED',
  APPROVAL_CHANGED: 'APPROVAL_CHANGED',
  TOO_MANY_ROUNDS: 'TOO_MANY_ROUNDS',
  INVALID_ROUND_AMOUNT: 'INVALID_ROUND_AMOUNT',
  INVALID_ROUND_DATE: 'INVALID_ROUND_DATE',
  ROUND_BEFORE_ADMISSION: 'ROUND_BEFORE_ADMISSION',
  ROUND_IN_FUTURE: 'ROUND_IN_FUTURE',
  DUPLICATE_ROUND_ID: 'DUPLICATE_ROUND_ID',
  REASON_REQUIRED: 'REASON_REQUIRED',
};

/**
 * The only two statuses `discharge/complete` (contract #3) ever lands on —
 * DISCHARGED (NORMAL/LAMA/DAMA/REFERRED) or DECEASED. A DISCHARGED/DECEASED
 * admission has an issued `bill_number` and reads GET /final-bill (locked).
 * CANCELLED is also terminal but was never discharged — it never gets a bill
 * number and stays on the live (read-only) running bill, so this is
 * DELIBERATELY narrower than `isTerminal`/`TERMINAL_STATUSES`. Single source
 * for the running-bill ↔ final-bill dispatch (RunningBillPanel, FE-3).
 */
export const DISCHARGE_COMPLETED_STATUSES = new Set([
  ADMISSION_STATUS.DISCHARGED,
  ADMISSION_STATUS.DECEASED,
]);
export const hasFinalBill = (status) => DISCHARGE_COMPLETED_STATUSES.has(status);

/**
 * Discharge/bill permission slugs (backend `role_permissions.py` is the
 * source of truth; mirrored here so the FE never inlines a role/slug
 * literal). `DISCHARGE_WITH_DUES` + `CORRECT_LOCKED_BILL` are ADMIN-only
 * (owner ruling 2 / Business §4 — not relaxed).
 */
export const DISCHARGE_PERMISSIONS = {
  DISCHARGE_PATIENT: 'DISCHARGE_PATIENT',
  DISCHARGE_WITH_DUES: 'DISCHARGE_WITH_DUES',
  CORRECT_LOCKED_BILL: 'CORRECT_LOCKED_BILL',
  GENERATE_IPD_BILL_PDF: 'GENERATE_IPD_BILL_PDF',
};

/**
 * TPA Bill ISSUE-action visibility (TPA-PREAUTH/tpa-bill, orchestrator
 * ruling 2026-09-18). A new issue is `409 ISSUE_AFTER_COMPLETION` by
 * contract (C2) once the admission is terminal — HIDE the "TPA Bill" issue
 * control at that point (never a 409-on-click). Stays visible through
 * DISCHARGE_INITIATED/BILLING_CLEARANCE (the final-for-claim bill can still
 * be issued). "Reprint" from the issued-bills list is a SEPARATE action,
 * untouched by this predicate — it stays available forever. This does NOT
 * check `GENERATE_IPD_BILL_PDF` itself — the caller combines this with its
 * own `usePermission(GENERATE_IPD_BILL_PDF)` check, same pattern as every
 * other permission-gated action in this file.
 */
/**
 * FIX-16 (business ALARM) — a TPA Bill names the insurer and the TPA it is
 * sent TO, so the ISSUE action needs both on file. FIX-14 can derive
 * CASHLESS from a payer-type pick without ever passing the claim-mode
 * form's own "insurer / TPA / policy required" rule, so this is checked at
 * the issue affordance too (server: 422 TPA_BILL_INSURER_REQUIRED).
 * Returns the DISABLED REASON, or '' when the bill may be issued.
 */
export const tpaBillBlockedReason = (admission) => {
  const a = admission || {};
  const missing = [];
  if (!String(a.insurer_name || '').trim()) missing.push('insurer');
  if (!String(a.tpa_name || '').trim()) missing.push('TPA');
  if (!missing.length) return '';
  return `Add the ${missing.join(' and ')} name on the insurance card before issuing a TPA bill.`;
};

export const canIssueTpaBill = (status, payerMode) =>
  !isTerminal(status) && payerMode === PAYER_MODE.CASHLESS;

/**
 * Machine error codes the three TPA-Bill routes raise (C2, verified against
 * the BUILT backend's `tpa_bill_service` at the contract reconcile). Plain
 * copy per code lives in `bill/billModel.js` (`tpaBillErrorMessage`) — the
 * wire `message` is never shown (contract: "no UI strings on the wire").
 * `ADMISSION_NOT_FOUND` is the SHARED code already in `DISCHARGE_ERROR_CODE`
 * and is not redefined here.
 */
export const TPA_BILL_ERROR_CODE = {
  NOT_CASHLESS: 'NOT_CASHLESS',
  ISSUE_AFTER_COMPLETION: 'ISSUE_AFTER_COMPLETION',
  SNAPSHOT_TOO_LARGE: 'SNAPSHOT_TOO_LARGE',
  TPA_BILL_NOT_FOUND: 'TPA_BILL_NOT_FOUND',
  // FIX-16 — the ISSUE door refuses a cashless admission with no insurer /
  // TPA named (reachable via FIX-14's payer-type derivation). Extra:
  // `missing: string[]`.
  TPA_BILL_INSURER_REQUIRED: 'TPA_BILL_INSURER_REQUIRED',
};

/**
 * Machine error codes the discharge / final-bill endpoints raise (frozen
 * contract #1-#14). Dialogs/models branch on THESE, never a free-hand
 * string comparison against `error.message`.
 */
export const DISCHARGE_ERROR_CODE = {
  ADMISSION_NOT_FOUND: 'ADMISSION_NOT_FOUND',
  INVALID_TRANSITION: 'INVALID_TRANSITION',
  NOT_PERMITTED_DUES: 'NOT_PERMITTED_DUES',
  BALANCE_NOT_ZERO: 'BALANCE_NOT_ZERO',
  BALANCE_CHANGED: 'BALANCE_CHANGED',
  DUES_NEGATIVE_BALANCE: 'DUES_NEGATIVE_BALANCE',
  CAUSE_OF_DEATH_REQUIRED: 'CAUSE_OF_DEATH_REQUIRED',
  REASON_REQUIRED: 'REASON_REQUIRED',
  LEDGER_DRIFT: 'LEDGER_DRIFT',
  USE_DISCHARGE_INTENT: 'USE_DISCHARGE_INTENT',
  NOT_PRIMARY_DOCTOR: 'NOT_PRIMARY_DOCTOR',
  BILL_NOT_ISSUED: 'BILL_NOT_ISSUED',
  ADMISSION_LOCKED: 'ADMISSION_LOCKED',
  NO_DUES: 'NO_DUES',
  AMOUNT_EXCEEDS_BALANCE: 'AMOUNT_EXCEEDS_BALANCE',
  CORRECTION_ALREADY_OPEN: 'CORRECTION_ALREADY_OPEN',
  NOT_LOCKED: 'NOT_LOCKED',
  CORRECTION_NOT_OPEN: 'CORRECTION_NOT_OPEN',
  // R3-4 (S4 EP-1) — the hard-block gate's two new machine errors, alongside
  // the existing BALANCE_NOT_ZERO (now charges-only). UNSETTLED_LAB_CREDIT
  // carries `unsettled_lab_paise` + `credit_entries`; REFUND_ADVANCE_FIRST
  // carries `refundable_advance_paise`.
  UNSETTLED_LAB_CREDIT: 'UNSETTLED_LAB_CREDIT',
  REFUND_ADVANCE_FIRST: 'REFUND_ADVANCE_FIRST',
  // B2 (BUGFIX/lama-discharge round 2) — the write-off exception's authoriser
  // gate (no extras; static copy — see dischargeErrorMessage).
  AUTHORISED_BY_REQUIRED: 'AUTHORISED_BY_REQUIRED',
  // death-record slice (invariant 4) — a death record exists and the
  // discharge attempted something inconsistent with it. Raised by BOTH
  // `discharge/initiate` (unconditionally) and `discharge/complete` (type !=
  // DECEASED). Never coerced to DECEASED.
  // CONTRACT RECONCILE (integration, 2026-08-27): the built server's
  // `DeathRecordedTypeLocked` carries extras `attempted_type:str|null` ONLY —
  // NOT `death_at`/`declaring_doctor_name`, which an earlier draft of this
  // comment claimed. Nothing reads them (`dischargeErrorMessage` maps the
  // code to static copy), so this is a comment correction, not a behaviour
  // change — but do not build on the old extras.
  DEATH_RECORDED_TYPE_LOCKED: 'DEATH_RECORDED_TYPE_LOCKED',
  // Cancel initiation (discharge-final BE-0) — the two NEW 409 refusals.
  CANCEL_INITIATION_FY_CLOSED: 'CANCEL_INITIATION_FY_CLOSED',
  CANCEL_INITIATION_INSURANCE_SETTLED: 'CANCEL_INITIATION_INSURANCE_SETTLED',
  // invoice-print-record — 403 when the invoice was printed before Complete
  // and the caller is not an admin.
  CANCEL_INITIATION_ADMIN_ONLY_AFTER_PRINT: 'CANCEL_INITIATION_ADMIN_ONLY_AFTER_PRINT',
  // death-record slice (invariant 6) — same string as
  // `DEATH_RECORD_ERROR_CODE.DEATH_RECORD_STALE` below (one family, two
  // doors), kept separate because callers key off a DIFFERENT error family
  // per endpoint (`dischargeErrorMessage` vs the death-record dialog's own
  // mapping).
  // CONTRACT RECONCILE (integration, 2026-08-27): the built
  // `discharge/complete` does NOT emit this. `_mint_and_commit` classifies a
  // raced death_rev loss internally, refreshes the row and RETRIES (the
  // guard is re-read per attempt), so the desk sees a success, not a 409.
  // The mapping below is therefore a defensive net for a shape the current
  // server cannot produce at this door — harmless, but do not treat it as a
  // reachable path when planning the next slice.
  DEATH_RECORD_STALE: 'DEATH_RECORD_STALE',
  // Owner 2026-10-13 — 409 at Initiate: live bill lines dated after the
  // discharge date being set. Extras: `discharge_date` (YYYY-MM-DD) +
  // `charges[{id, description, service_date, amount_paise?, source, movable,
  // allowed_actions, default_action, blocked_code, is_room_charge}]` (`amount_paise`
  // absent for a money-hidden caller; the server decides what each line may do).
  CHARGES_AFTER_DISCHARGE_DATE: 'CHARGES_AFTER_DISCHARGE_DATE',
  // 403 {fix_date} — non-admin Initiate/re-initiate later than the day charges were fixed for.
  DISCHARGE_DATE_AFTER_CHARGE_FIX: 'DISCHARGE_DATE_AFTER_CHARGE_FIX',
  // DISCHARGE-TEMPLATES Phase 1, FE-1 (§2.3 frozen contract) — the reshaped
  // discharge-summary PUT's `422`/`413` validation codes. NOT_PERMITTED and
  // IPD_TABLE_UNAVAILABLE are the summary GET/PUT's own generic-error codes
  // (§2.2/§2.3); the existing ADMISSION_LOCKED/ADMISSION_NOT_FOUND above are
  // reused verbatim, not duplicated. Extend this ONE map — never a second
  // discharge-summary-only code enum.
  UNKNOWN_FIELD_KEY: 'UNKNOWN_FIELD_KEY',
  READ_ONLY_FIELD: 'READ_ONLY_FIELD',
  FIELD_HIDDEN: 'FIELD_HIDDEN',
  FIELD_TOO_LONG: 'FIELD_TOO_LONG',
  INVALID_DATETIME: 'INVALID_DATETIME',
  INVALID_DATE_RANGE: 'INVALID_DATE_RANGE',
  SUMMARY_TOO_LARGE: 'SUMMARY_TOO_LARGE',
  NOT_PERMITTED: 'NOT_PERMITTED',
  IPD_TABLE_UNAVAILABLE: 'IPD_TABLE_UNAVAILABLE',
  // verdict-r2.md ruling C-3 — a 422 the row-shape validator raises on the
  // `investigations` (`lab_lines`) key when a row itself is malformed
  // (distinct from FIELD_TOO_LONG, which is a length cap). Carries `key`
  // (always `investigations` today) and may carry `row` (0-based index).
  INVALID_FIELD_SHAPE: 'INVALID_FIELD_SHAPE',
  // DISCHARGE-TEMPLATES Phase 1, owner ruling H (2026-09-13) — no discharge
  // summary write/print and no discharge initiate/complete without a
  // treating (primary) doctor on the admission. Raised by the summary PUT,
  // the summary PDF, and discharge/initiate + discharge/complete alike — ONE
  // code, reused verbatim across every door (never a second enum member per
  // route).
  DOCTOR_REQUIRED: 'DOCTOR_REQUIRED',
  // FIX-1 (R12.1, compliance report) — the `discharge/complete` C3 guard
  // table's 4th (role-blind) planned-discharge guard, sibling to the
  // pre-existing PLANNED_DISCHARGE_IN_FUTURE/_BACKDATED_ADMIN_ONLY/_FY_MISMATCH
  // client-side mirror in `dischargeModel.js`. Carries `days_back`/`limit_days`.
  PLANNED_DISCHARGE_TOO_OLD: 'PLANNED_DISCHARGE_TOO_OLD',
  // bk-1336 (integration glue, rework round 1) — the other three C3
  // planned-discharge guards. `buildDischargeGate` pre-empts all of them for
  // NORMAL/REFERRED, so they only reach the wire on a stale-plan race; mapped
  // so that race can never print a raw server code at the moment of an
  // irreversible action.
  PLANNED_DISCHARGE_IN_FUTURE: 'PLANNED_DISCHARGE_IN_FUTURE',
  PLANNED_DISCHARGE_BACKDATED_ADMIN_ONLY: 'PLANNED_DISCHARGE_BACKDATED_ADMIN_ONLY',
  PLANNED_DISCHARGE_FY_MISMATCH: 'PLANNED_DISCHARGE_FY_MISMATCH',
  // DOD rule v3 (C5) — `discharge/complete` for NORMAL/REFERRED refuses a
  // confirmed date on a DIFFERENT IST calendar day from `now` when no
  // `discharge_stamp` ("PLANNED"|"NOW") was sent. Carries
  // `planned_discharge_at`, `now`, `days_off` (signed int — `hours_off` is
  // DROPPED with the ±2 h tolerance). The FE re-derives the choice from
  // `planned_discharge_at`/`now` (single source) and never reads the echoed
  // gap as anything but a display convenience.
  DISCHARGE_STAMP_CHOICE_REQUIRED: 'DISCHARGE_STAMP_CHOICE_REQUIRED',
  // DOD rule v3 (C2) — the ONE shared confirmed-date guard ladder, raised by
  // all THREE callers (discharge/initiate · PATCH /admissions/{id} edit ·
  // discharge/complete). Ladder order is FIXED and the FIRST failure wins, so
  // exactly one of these ever reaches the screen per attempt. Extras:
  // BEFORE_ADMISSION {admitted_at} · IN_FUTURE {today, planned_discharge_at} ·
  // ADMIN_ONLY {days_back, limit_days:7, earliest} (403) · TOO_OLD {days_back} ·
  // FY_MISMATCH {fy}. Client-side mirror: `dischargeModel.validateConfirmedDod`.
  DISCHARGE_DATE_BEFORE_ADMISSION: 'DISCHARGE_DATE_BEFORE_ADMISSION',
  DISCHARGE_DATE_IN_FUTURE: 'DISCHARGE_DATE_IN_FUTURE',
  DISCHARGE_DATE_ADMIN_ONLY: 'DISCHARGE_DATE_ADMIN_ONLY',
  DISCHARGE_DATE_TOO_OLD: 'DISCHARGE_DATE_TOO_OLD',
  DISCHARGE_DATE_FY_MISMATCH: 'DISCHARGE_DATE_FY_MISMATCH',
  // DOD rule v3 (C3) — the EDIT door's own two codes. FY_LOCKED (409) is the
  // FY failure on an edit: its copy names the already-minted invoice, so it
  // is a distinct code from FY_MISMATCH above (extras {fy,
  // invoice_number_printed}). REASON_REQUIRED (422) is deliberately NOT the
  // generic `REASON_REQUIRED` — that code is already the cancel-admission and
  // insurance-decrease guard, and a third overload would make one FE message
  // map impossible (build-plan CONTRACT DELTAS #2).
  DISCHARGE_DATE_FY_LOCKED: 'DISCHARGE_DATE_FY_LOCKED',
  DISCHARGE_DATE_REASON_REQUIRED: 'DISCHARGE_DATE_REASON_REQUIRED',
  // DOD rule v3 (C3) — a confirmed-date edit attempted after the discharge
  // completed (409). CONTRACT ASSUMPTION: the build-plan names this existing
  // code `DISCHARGE_DATE_NOT_EDITABLE` while FIX-7's summary-PUT door already
  // ships as `DISCHARGE_DATE_READ_ONLY` below — different doors, different
  // ratified sentences, so both are mapped. Reconcile against the built
  // OpenAPI at integration.
  DISCHARGE_DATE_NOT_EDITABLE: 'DISCHARGE_DATE_NOT_EDITABLE',
  // FIX-7 — the summary PUT refuses a NON-EMPTY `discharged_at` once the
  // stay is completed (the date was recorded at discharge; a correction is
  // a re-signed corrected copy). Carries `discharged_at`. Unreachable from
  // this UI (FIX-7-FE never sends the key, and the field renders read-only
  // on a terminal admission) — mapped anyway so a stale-tab or chat/voice
  // caller can never surface a raw server code.
  DISCHARGE_DATE_READ_ONLY: 'DISCHARGE_DATE_READ_ONLY',
};

/**
 * Ruling H (owner, 2026-09-13, decisions/discharge-templates-phase1 addendum
 * H) — the ONE ratified sentence for "no treating doctor assigned", reused
 * VERBATIM everywhere the rule surfaces: the discharge-summary tab's banner +
 * Save/Print disabled reason, the discharge-summary PUT's 409 DOCTOR_REQUIRED
 * fallback (`admissionDischargeApi.js`), Single source — business
 * ratifies; never re-typed per call site. The Discharge DIALOG uses its own
 * sentence (`DOCTOR_REQUIRED_DISCHARGE_COPY` below) — same rule, different
 * surface, so a different reason.
 */
export const DOCTOR_REQUIRED_COPY = 'Assign a treating doctor to this admission before writing the discharge summary.';

/**
 * The SAME rule, stated for the DISCHARGE surface (integration reconcile,
 * r4). `DOCTOR_REQUIRED_COPY` above names the discharge SUMMARY, which is
 * the wrong reason on the Discharge dialog — there the user is trying to
 * discharge the patient, not write a sheet. One rule, two surfaces, two
 * sentences; both need business ratification. The backend's own 409
 * `message` is deliberately generic ("…before continuing.") and is never
 * rendered — the FE maps the CODE to its own copy.
 */
export const DOCTOR_REQUIRED_DISCHARGE_COPY = 'Assign a treating doctor to this admission before discharge.';

/**
 * doctorDisplayName — the ONE shared helper (copy-ratified-slice1.md, "Shared
 * helper" — business stakeholder, 2026-09-17) that prefixes a raw doctor name
 * with "Dr " for display, UNLESS the name already starts with "Dr"/"Dr."
 * (case-insensitive) — so "Dr Dr" is structurally impossible regardless of
 * how the name was typed/stored. Empty/blank input returns `''` (the
 * caller's own fallback sentence applies, never a re-typed "Dr" alone).
 * Used verbatim by every doctor-bearing discharge-template string (toast,
 * popover headers, helper/disabled-reason copy, confirms) — never
 * duplicated inline in a component.
 */
export const doctorDisplayName = (name) => {
  const n = String(name || '').trim();
  if (!n) return '';
  return /^dr\.?\s/i.test(n) ? n : `Dr ${n}`;
};

/**
 * snippetLimitReachedCopy — the ONE sentence for "this field is at its
 * saved-template cap", shared by the server-driven `SNIPPET_LIMIT_REACHED`
 * 4xx (`services/api/dischargeSnippetApi.js`) and the FE's own pre-emptive
 * "Save as template" disabled reason (`SummaryFields.jsx`) — so a doctor
 * never reads two different sentences for the same cause depending on
 * whether the block was caught client-side or server-side (copy-ratified-
 * slice1.md §A #4/#8b). `limit` absent/null falls back to the no-limit
 * sentence (the server's own template-limit is unknown yet).
 */
export function snippetLimitReachedCopy(limit) {
  return limit != null
    ? `This field already has ${limit} templates. Delete one before saving another.`
    : 'This field already has the maximum number of templates. Delete one before saving another.';
}

/**
 * Machine error codes `PUT /admissions/{id}/death-record` (EP-D1) and
 * `DELETE /admissions/{id}/death-record` (EP-D2) raise — the
 * `DeathRecordServiceError` family, distinct from `DISCHARGE_ERROR_CODE`
 * above (a different endpoint / error family per the frozen contract).
 * `RecordDeathDialog` branches on THESE, never a free-hand string
 * comparison against `error.message`.
 */
export const DEATH_RECORD_ERROR_CODE = {
  ADMISSION_NOT_FOUND: 'ADMISSION_NOT_FOUND',
  // `death_rev` sent != stored (incl. someone else recorded/removed first).
  // Extras: `death_rev:int` (the stored value).
  DEATH_RECORD_STALE: 'DEATH_RECORD_STALE',
  // status ∉ DECEASED_FROM_STATUSES (terminal, or never admitted). Extras: `status:str`.
  ADMISSION_CLOSED: 'ADMISSION_CLOSED',
  // Extras: `reason: "BEFORE_ADMISSION"|"IN_FUTURE"|"UNPARSEABLE"`, `admitted_at:str`, `death_at:str`.
  DEATH_AT_INVALID: 'DEATH_AT_INVALID',
  // declaring doctor member missing / not ACTIVE / role != DOCTOR. Extras: `declaring_doctor_member_id:str`.
  DECLARING_DOCTOR_INVALID: 'DECLARING_DOCTOR_INVALID',
  // EP-D2 (remove) only — no death record exists to remove.
  NO_DEATH_RECORD: 'NO_DEATH_RECORD',
};

// ═══════════════════════════════════════════════════════════════════════
// Dues collection (C6b — mirror of the frozen §API CONTRACT,
// state/epics/C/C6b/build-plan.md). `admission_state.py` DUES_STATUS_OPEN/
// CLOSED/WRITTEN_OFF is the backend source of truth; mirrored here verbatim
// so no dues surface ever inlines the string literal.
// ═══════════════════════════════════════════════════════════════════════

/** `dues_status` on an admission row / dues-collect / write-off response. */
export const DUES_STATUS = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  WRITTEN_OFF: 'WRITTEN_OFF',
};

/**
 * Dues permission slugs (backend `role_permissions.py` is the source of
 * truth; mirrored here so the FE never inlines a literal). COLLECT_DUES =
 * reception/billing/admin; WRITE_OFF_DUES = admin only (segregation of
 * duties, same tier as REFUND_DEPOSIT).
 */
export const DUES_PERMISSIONS = {
  COLLECT_DUES: 'COLLECT_DUES',
  WRITE_OFF_DUES: 'WRITE_OFF_DUES',
};

/**
 * Machine error codes the dues/collect + dues/write-off endpoints raise
 * (frozen contract §1/§3). Dialogs branch on THESE, never a free-hand string
 * comparison against `error.message`.
 */
export const DUES_ERROR_CODE = {
  ADMISSION_NOT_FOUND: 'ADMISSION_NOT_FOUND',
  DUES_NOT_OPEN: 'DUES_NOT_OPEN',
  DUES_CHANGED: 'DUES_CHANGED',
  AMOUNT_EXCEEDS_DUES: 'AMOUNT_EXCEEDS_DUES',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
  INVALID_METHOD: 'INVALID_METHOD',
  REFERENCE_REQUIRED: 'REFERENCE_REQUIRED',
  REASON_REQUIRED: 'REASON_REQUIRED',
  WRITEOFF_NOT_FOUND: 'WRITEOFF_NOT_FOUND',
  IDEMPOTENCY_KEY_REQUIRED: 'IDEMPOTENCY_KEY_REQUIRED',
  DEPENDENCY_UNAVAILABLE: 'DEPENDENCY_UNAVAILABLE',
};

/**
 * Aging buckets (display-only — owner ruling: no reminder/SMS affordance
 * anywhere) for a discharged admission's days-outstanding. Bucket keys are
 * the SINGLE SOURCE the Payment Pending queue's row badges + aging filter
 * both read, so the two can never disagree.
 */
export const DUES_AGING_BUCKET = {
  DAYS_0_30: '0-30',
  DAYS_31_60: '31-60',
  DAYS_61_90: '61-90',
  DAYS_90_PLUS: '90+',
};

/**
 * IST calendar date ('YYYY-MM-DD') for an arbitrary timestamp/Date. Mirrors
 * `utils/dateIST.js`'s `getTodayIST` approach (IST/UTC day-boundary rule —
 * never `toISOString().slice(0,10)`, which mis-buckets between IST midnight
 * and 05:30).
 */
export function istCalendarDate(input) {
  if (input == null) return null;
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

/**
 * duesAgingDays — WHOLE calendar days outstanding since `discharged_at`,
 * counted by IST calendar date (never raw millisecond difference, which
 * drifts across the IST/UTC day boundary — see `feedback` memory rule).
 * Returns null when `discharged_at` is absent/unparseable. Single source
 * consumed by the Payment Pending queue row + any other aging display.
 */
export function duesAgingDays(dischargedAt, now = new Date()) {
  const dischargeDay = istCalendarDate(dischargedAt);
  const today = istCalendarDate(now);
  if (!dischargeDay || !today) return null;
  const start = new Date(`${dischargeDay}T00:00:00Z`).getTime();
  const end = new Date(`${today}T00:00:00Z`).getTime();
  return Math.max(0, Math.round((end - start) / 86400000));
}

/**
 * duesAgingBucket — classify a days-outstanding figure into one of the four
 * ratified buckets (0–30 / 31–60 / 61–90 / 90+). Returns null for a null
 * `days` (no `discharged_at`).
 */
export function duesAgingBucket(days) {
  if (days == null) return null;
  if (days <= 30) return DUES_AGING_BUCKET.DAYS_0_30;
  if (days <= 60) return DUES_AGING_BUCKET.DAYS_31_60;
  if (days <= 90) return DUES_AGING_BUCKET.DAYS_61_90;
  return DUES_AGING_BUCKET.DAYS_90_PLUS;
}

// DISCHARGE-TEMPLATES 1b Slice 1 (AGE-FE) — the patient `age` field's
// FROZEN validation contract (AGE-EDIT-spec.md §3.1), mirrored verbatim
// client-side so the dialog rejects the same forms the server would 422 on,
// inline, before a round-trip. Single source: `PatientDetailsDialog` (and
// any future age editor) imports this, never re-types the pattern.
// Accepts years 0-999 ("45"), or a count + m/d unit ("6m", "18d") — max 4
// characters total.
export const AGE_PATTERN = /^(\d{1,3}|\d{1,2}[mMdD])$/;
export const AGE_MAX_LENGTH = 4;
// Ratified copy (copy-ratified-slice1.md §A #20/#21).
export const AGE_FORMAT_HELPER_COPY = 'Age in years (45). For a baby, type 6m for 6 months or 18d for 18 days.';
export const AGE_INVALID_COPY = 'Enter age in years (45), or 6m / 18d for a baby under one year.';

/**
 * isValidAge — true for `null`/empty (clearing the field is always valid)
 * or a string within `AGE_MAX_LENGTH` matching `AGE_PATTERN`. The ONE
 * client-side gate `PatientDetailsDialog` checks before calling the PATCH —
 * mirrors, never replaces, the server's own regex/length validation.
 */
export function isValidAge(value) {
  if (value == null || value === '') return true;
  const trimmed = String(value).trim();
  if (trimmed === '') return true;
  return trimmed.length <= AGE_MAX_LENGTH && AGE_PATTERN.test(trimmed);
}

// ── Admission access (money rule + department wall + care team) ──────────
/**
 * `money_visible` rides every `AdmissionResponse` (admission-access C-2).
 * `false` = the server withheld every money field (they arrive `null`) — every
 * surface must then show NO money at all, never ₹0. `undefined` (an older
 * server) is NOT hidden: today's slug-gated behaviour applies. ONE predicate —
 * the rail, the workspace and every money surface read this, never
 * `money_visible === false` inline.
 */
export const isMoneyHidden = (admission) => !!admission && admission.money_visible === false;

/**
 * Machine error codes the access gates raise (admission-access C-1, frozen).
 * Shared codes reuse their existing constant so each literal lives once.
 */
export const ADMISSION_ACCESS_ERROR_CODE = {
  OUT_OF_DEPARTMENT_SCOPE: 'OUT_OF_DEPARTMENT_SCOPE',
  PROVIDER_OUT_OF_SCOPE: 'PROVIDER_OUT_OF_SCOPE',
  MONEY_NOT_PERMITTED: 'MONEY_NOT_PERMITTED',
  NOT_PRIMARY_DOCTOR: DISCHARGE_ERROR_CODE.NOT_PRIMARY_DOCTOR,
  NOT_TREATING_DOCTOR: 'NOT_TREATING_DOCTOR',
  CARE_TEAM_NOT_PERMITTED: 'CARE_TEAM_NOT_PERMITTED',
  DOCTOR_INVALID: 'DOCTOR_INVALID',
  ALREADY_PRIMARY: 'ALREADY_PRIMARY',
  CARE_TEAM_LIMIT: 'CARE_TEAM_LIMIT',
  CARE_TEAM_CONFLICT: 'CARE_TEAM_CONFLICT',
  ADMISSION_TERMINAL: PLAN_DISCHARGE_ERROR_CODE.ADMISSION_TERMINAL,
  DUPLICATE_ACTIVE_ADMISSION: 'DUPLICATE_ACTIVE_ADMISSION',
};

/** Workspace "why is there no bill" lines (nurse vs doctor) — also the money-route refusal copy. */
export const BILL_HINT_DOCTOR_COPY = 'Bill details are visible to the primary doctor and the front desk.';
export const BILL_HINT_NURSE_COPY = 'Bill details are visible to the front desk.';

/**
 * The care-team cap sentence — `limit` comes from the 409's extra `limit`
 * (contract C-1); absent → 10, the frozen server cap. Also the disabled
 * reason on "+ Add doctor" once the cap is reached (one sentence, both places).
 */
export const CARE_TEAM_DEFAULT_LIMIT = 10;
export const careTeamLimitCopy = (limit) =>
  `This patient already has ${limit ?? CARE_TEAM_DEFAULT_LIMIT} added doctors, which is the limit. Remove one to add another.`;

/**
 * Surface copy for the access-gate codes — the wire `message` is never UI
 * copy. Scoped to the endpoints C-1 lists (never key a global map by code:
 * `ADMISSION_TERMINAL` here is the care-team door's copy, the plan-discharge
 * door keeps its own). "front desk" per the ratified mockups (billing staff
 * are receptionists). Every string is PO-ratified (copy-ratified.md).
 */
/**
 * Out-of-department copy is role-aware: only a DOCTOR can be added to a care
 * team, so only a doctor is told to ask for that. Everyone else is pointed at
 * the organisation admin. `ADMISSION_ACCESS_ERROR_COPY[OUT_OF_DEPARTMENT_SCOPE]`
 * stays the doctor sentence; surfaces pick via `outOfDepartmentCopy(role)`.
 */
// Owner rule (2026-10-13): a staff member whose department memberships are ALL
// deactivated sees no admissions until an admin assigns a new department.
export const DEPT_ACCESS_BLOCKED_COPY =
  'Your department is no longer active. Ask your organisation admin to assign you a department.';
export const OUT_OF_DEPARTMENT_COPY_OTHER_ROLES =
  'This admission belongs to another department. Ask your organisation admin if you need access.';
export const outOfDepartmentCopy = (role) => (
  role === STAFF_ROLES.DOCTOR
    ? ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE]
    : OUT_OF_DEPARTMENT_COPY_OTHER_ROLES
);

export const ADMISSION_ACCESS_ERROR_COPY = {
  [ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE]:
    'This admission belongs to another department. To see it, ask its primary doctor, the duty doctor, reception or your organisation admin to add you to the care team.',
  [ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE]:
    'This doctor is in another department. Pick another doctor.',
  [ADMISSION_ACCESS_ERROR_CODE.MONEY_NOT_PERMITTED]: BILL_HINT_NURSE_COPY,
  [ADMISSION_ACCESS_ERROR_CODE.NOT_PRIMARY_DOCTOR]: BILL_HINT_DOCTOR_COPY,
  [ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR]:
    "View only. Only this admission's treating doctors and the front desk can save this summary.",
  [ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_NOT_PERMITTED]:
    "You can't change this patient's doctors.",
  [ADMISSION_ACCESS_ERROR_CODE.DOCTOR_INVALID]:
    "This doctor can't be added. They may no longer be active. Pick another doctor.",
  [ADMISSION_ACCESS_ERROR_CODE.ALREADY_PRIMARY]:
    "This is the patient's primary doctor. To replace them, use Change doctor.",
  [ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_LIMIT]: careTeamLimitCopy(),
  // The screen refetches the admission BEFORE showing this (sentence 2 is only
  // true after the refetch).
  [ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_CONFLICT]:
    "Someone else just changed this patient's doctors. The list is now up to date. Please try again.",
  [ADMISSION_ACCESS_ERROR_CODE.DUPLICATE_ACTIVE_ADMISSION]: 'This patient already has an active admission.',
  [ADMISSION_ACCESS_ERROR_CODE.ADMISSION_TERMINAL]:
    "Doctors can't be changed after the discharge summary is locked.",
};

/**
 * PROVIDER_OUT_OF_SCOPE on CREATE, per attending-provider role. Register Visit
 * walls the ATTENDING provider (B-BE-6b), so its nurse / lab-tech legs need
 * their own sentence. The doctor entry IS the code's entry above.
 */
export const PROVIDER_OUT_OF_SCOPE_COPY_BY_ROLE = {
  [STAFF_ROLES.DOCTOR]: ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE],
  [STAFF_ROLES.NURSE]: 'This nurse is in another department. Pick another nurse.',
  [STAFF_ROLES.LAB_TECHNICIAN]: 'This lab technician is in another department. Pick another lab technician.',
};

/**
 * PROVIDER_OUT_OF_SCOPE on an EXISTING appointment (cancel / rebook / time
 * edit / check-in) — only reachable from a stale screen.
 */
export const PROVIDER_OUT_OF_SCOPE_APPOINTMENT_COPY =
  'This appointment is with a doctor in another department. Refresh the page to see your current list.';

/**
 * Change doctor — label of the department FILTER above the doctor list (owner
 * fix bk-1678, 2026-10-05). The wording is not in copy-ratified.md: PO to ratify.
 */
export const CHANGE_DOCTOR_DEPT_FILTER_LABEL = 'Filter doctors by department';

/**
 * S13 scope caption (un-deferred by the owner 2026-10-06). Copy is the owner-accepted
 * mockup 4 / index.html "Receptionist caption": "Showing: Orthopaedics + No department +
 * Emergency"; more than two departments → the first two, then "+n more".
 * Not in copy-ratified.md (it was deferred with the feature): PO to ratify.
 */
export const SCOPE_CAPTION_LEAD = 'Showing:';
export const SCOPE_CAPTION_NAMED_MAX = 2;
// Walled DOCTOR only: care-team admissions from other departments are visible too.
export const SCOPE_CAPTION_OWN_PATIENTS = ' + your patients';
export const scopeCaptionScope = (deptNames) => {
  const names = Array.isArray(deptNames) ? deptNames : [];
  const more = names.length - SCOPE_CAPTION_NAMED_MAX;
  return `${names.slice(0, SCOPE_CAPTION_NAMED_MAX).join(', ')}${more > 0 ? ` +${more} more` : ''} + No department + Emergency`;
};

/**
 * Change doctor — hint shown once a DIFFERENT doctor is picked (copy-ratified.md
 * #39, PO-ratified). With a current primary the old one is removed from the
 * care team (not just demoted), so the sentence says so and names the way back.
 * Names arrive already through `doctorDisplayName` (or the unnamed fallback).
 */
export const changePrimaryHintCopy = (newName, oldName) => (
  oldName
    ? `${newName} will see this patient's bill. ${oldName} will be removed from this patient's doctors. You can add them back with Add doctor.`
    : `${newName} will see this patient's bill.`
);

/**
 * NOT_TREATING_DOCTOR on the DISCHARGE steps — its own sentence (the shared
 * map above says "…can save this summary", wrong on this surface). Same
 * sentence for the disabled reason and for the 403.
 */
export const NOT_TREATING_DOCTOR_DISCHARGE_COPY =
  "Only this patient's treating doctors or the front desk can discharge this patient.";

/** Route-gate / pane copy (admission-access S1; PO-ratified). */
export const NO_ACCESS_ADMISSION_COPY = "You don't have access to Manage Admission. Ask your organisation admin if you need it.";
export const NO_ACCESS_ADMIT_COPY = "You don't have access to admit patients. Ask your organisation admin if you need it.";
export const NO_ACCESS_ABHA_COPY = "You don't have access to ABHA. Ask your organisation admin if you need it.";
export const ACCESS_CHANGED_COPY = 'Your access to Manage Admission has changed. Ask your organisation admin if you need it.';
export const NO_PATIENT_PANE_PLACEHOLDER_COPY = 'Select a patient from the list to see their details.';

/**
 * Care team (admission-access slice M) — PO-ratified copy (copy-ratified.md
 * #32-#38, #40 and ADDENDUM #45-#58, 2026-10-05). Single source (Rule D):
 * TreatingDoctorsCard, AddTreatingDoctorDialog, ViewerRelationshipChip,
 * deskFilters and IpdPage import these; `admissionCopyRatified.test.js` pins
 * the exact text. On screen the terms stay "Primary doctor" / "Added doctor"
 * (owner ruling); only the print says "Treating consultant".
 */
// #47 — a doctor the server sent with no name; never prefixed "Dr".
export const CARE_TEAM_UNNAMED_DOCTOR = 'Unnamed doctor';
// #40 / #51
export const TREATING_DOCTORS_HINT_COPY = "Only the primary doctor can see this patient's bill. Added doctors see clinical details only.";
// #35 / #65
export const NO_ADDED_DOCTORS_COPY = 'No added doctors yet.';
export const ADD_DOCTOR_HELPER_COPY = 'Added doctors see this patient in their list and can edit the discharge summary. They cannot see the bill.';
// #50
export const NO_PRIMARY_DOCTOR_COPY = "No primary doctor yet. Assign one so a doctor can see this patient's bill.";
// #52 — the card's button matches the taskbar label and #2's sentence.
export const CHANGE_DOCTOR_LABEL = 'Change doctor';
// #37 / #38 / #54
export const REMOVE_DOCTOR_KEEP_LABEL = 'Keep doctor';
export const REMOVE_DOCTOR_CTA_LABEL = 'Remove doctor';
export const removeDoctorTitle = (name) => `Remove ${name} from this patient's doctors?`;
export const REMOVE_YOURSELF_TITLE = "Remove yourself from this patient's doctors?";
export const REMOVE_DOCTOR_BODY = 'They will no longer see this patient in their list or edit the discharge summary. Anything they already wrote stays on the record.';
export const REMOVE_YOURSELF_BODY = 'You will no longer see this patient in your list or edit the discharge summary. Anything you already wrote stays on the record. The front desk or another treating doctor can add you back.';
export const REMOVE_DOCTOR_FAILED_COPY = 'Couldn’t remove the doctor. Nothing was changed. Try again.';
// #36 — rows already on the team, shown disabled with why.
export const ADD_DOCTOR_ROW_PRIMARY_LABEL = 'Primary doctor';
export const ADD_DOCTOR_ROW_ADDED_LABEL = 'Already added';
// #56
export const ADD_DOCTOR_SEARCH_PLACEHOLDER = 'Search by name or specialisation…';
// #55 — never the internal patient id.
export const addDoctorsTitle = (patientName) => (patientName ? `Add doctors — ${patientName}` : 'Add doctors');
// #46 — nothing went through: singular for one pick, plural otherwise.
export const addDoctorFailedCopy = (picks) => (picks === 1
  ? 'Couldn’t add the doctor. Nothing was changed. Try again.'
  : 'Couldn’t add the doctors. Nothing was changed. Try again.');
// #45 — some picks went through, a later one failed.
export const ADD_DOCTOR_PARTIAL_FAILED_COPY = 'Couldn’t add every doctor. Some were added and show “Already added”. Try again for the rest.';
// #32 (rail / IPD row tags) · #57 (workspace header / IPD details "you" chip)
export const VIEWER_RELATIONSHIP_TAG = { PRIMARY: 'Primary', ADDED: 'Added' };
export const VIEWER_RELATIONSHIP_YOU_TEXT = { PRIMARY: 'You · Primary doctor', ADDED: 'You · Added doctor' };
// #34 · #58 (IPD page, My admissions)
export const MINE_EMPTY_TITLE = 'You are not a treating doctor on any admission yet.';
export const MINE_EMPTY_BODY = 'When you are the primary or an added doctor, the admission appears here.';
export const MINE_SEE_ALL_COPY = 'See all admitted patients';

/** "Admitted · another department" — the patient-lookup row label (no link, IP no. or department). */
export const ADMITTED_ELSEWHERE_COPY = 'Admitted · another department';

/**
 * PatientHistory — visits the viewer's department wall hides. Count only (no
 * dates, doctors or department names). Nothing for n = 0.
 */
export const hiddenVisitsLine = (n) => (n === 1
  ? '1 visit in another department is not shown.'
  : `${n} visits in other departments are not shown.`);
export const NO_VISITS_IN_YOUR_DEPARTMENTS_COPY = 'No visits in your departments yet.';

/** Deposits report — caption shown only when the server scoped the report to the viewer's departments. */
export const DEPOSITS_REPORT_DEPT_SCOPED_COPY = 'Showing your departments only';

/**
 * Discharge — the front-desk-only wording for a caller whose money is withheld
 * (no amount, no ₹0). Both PO-ratified.
 *  - NO_BILL_CONFIRM_BODY: the Complete confirm.
 *  - BILL_OPEN_NO_AMOUNT_COPY: every amount-bearing refusal (BALANCE_NOT_ZERO,
 *    NOT_PERMITTED_DUES, UNSETTLED_LAB_CREDIT, REFUND_ADVANCE_FIRST,
 *    DUES_NEGATIVE_BALANCE).
 */
export const NO_BILL_CONFIRM_BODY = 'The bed will be freed and the final bill number issued. This cannot be undone.';
export const BILL_OPEN_NO_AMOUNT_COPY = 'The front desk needs to settle the bill before this discharge can be completed.';

/**
 * Admit Patient — the DUPLICATE_ACTIVE_ADMISSION 409 when the existing
 * admission is outside the viewer's departments (GAP-H1 / S7-AC12): IP no. and
 * department name only (no doctor, bed or dates), never a link. Returns the
 * lines to render (join only the parts present; line 2 only with a department).
 */
export const duplicateOutOfScopeLines = ({ ip_number: ipNumber, department_name: departmentName }) => {
  const first = [ipNumber ? `IP no. ${ipNumber}` : null, departmentName || null].filter(Boolean).join(' · ');
  return [first || null, departmentName ? `Contact the ${departmentName} front desk.` : null].filter(Boolean);
};

/**
 * Cancel initiation (discharge-final FE-0) — PO copy K8/K13, keyed by the codes
 * of `POST /discharge/cancel-initiation` ONLY (a different endpoint's
 * REASON_REQUIRED/INVALID_TRANSITION copy must not be reused). Unmapped codes
 * (404, anything else) fall back to the server message at the call site.
 * INVALID_TRANSITION is the race with Complete (K13); OUT_OF_DEPARTMENT_SCOPE is
 * role-dependent (see `outOfDepartmentCopy`) so it is resolved in the function.
 */
export const CANCEL_INITIATION_ADMIN_ONLY_COPY = 'Only an admin can cancel after the invoice has been printed.';
export const CANCEL_INITIATION_ERROR_COPY = {
  [DISCHARGE_ERROR_CODE.CANCEL_INITIATION_ADMIN_ONLY_AFTER_PRINT]: CANCEL_INITIATION_ADMIN_ONLY_COPY,
  [DISCHARGE_ERROR_CODE.CANCEL_INITIATION_FY_CLOSED]:
    'This invoice belongs to the previous financial year, so the initiation cannot be cancelled.',
  [DISCHARGE_ERROR_CODE.CANCEL_INITIATION_INSURANCE_SETTLED]:
    'This bill has an insurance settlement, so the initiation cannot be cancelled.',
  [DISCHARGE_ERROR_CODE.DEATH_RECORDED_TYPE_LOCKED]:
    'A death is recorded for this patient. Complete the discharge as Deceased instead.',
  [DISCHARGE_ERROR_CODE.INVALID_TRANSITION]:
    'This admission was just updated by someone else. Close and reopen to see its current state.',
  [DISCHARGE_ERROR_CODE.REASON_REQUIRED]: 'A reason is required to cancel the initiation.',
  [ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR]:
    "Only this patient's treating doctors or the front desk can discharge this patient.",
};
export const cancelInitiationErrorCopy = (code, role) => (
  code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE
    ? outOfDepartmentCopy(role)
    : CANCEL_INITIATION_ERROR_COPY[code] || null
);

// Owner 2026-10-13 — per-line Remove / Move on the CHARGES_AFTER_DISCHARGE_DATE
// refusal. POST /admissions/{id}/charges/fix-after-discharge-date
// {discharge_date: YYYY-MM-DD, items[{id, action}], reason_code, reason?}. The
// server stores its own sentence for the two fixed codes; OTHER sends the typed text.
export const FIX_CHARGES_ACTION = { MOVE: 'MOVE', REMOVE: 'REMOVE' };
export const FIX_CHARGES_BLOCKED_CODE = { LAB_ALREADY_DONE: 'LAB_ALREADY_DONE' };
export const MOVE_CHARGES_REASON = {
  ENTERED_AFTER_PATIENT_LEFT: 'ENTERED_AFTER_PATIENT_LEFT',
  WRONG_DATE_ENTERED: 'WRONG_DATE_ENTERED',
  OTHER: 'OTHER',
};
export const MOVE_CHARGES_REASON_OPTIONS = [
  { code: MOVE_CHARGES_REASON.ENTERED_AFTER_PATIENT_LEFT, label: 'Charge was entered after the patient left' },
  { code: MOVE_CHARGES_REASON.WRONG_DATE_ENTERED, label: 'Wrong date entered on the charge' },
  { code: MOVE_CHARGES_REASON.OTHER, label: 'Other' },
];
export const FIX_CHARGES_ERROR_CODE = {
  REASON_REQUIRED: 'REASON_REQUIRED',
  MOVE_NOT_ALLOWED_STATUS: 'MOVE_NOT_ALLOWED_STATUS',
  CHARGE_NOT_FOUND: 'CHARGE_NOT_FOUND',
  CHARGE_REVERSED: 'CHARGE_REVERSED',
  CHARGE_NOT_AFTER_DISCHARGE_DATE: 'CHARGE_NOT_AFTER_DISCHARGE_DATE',
  CHARGES_CHANGED: 'CHARGES_CHANGED',
  ACTION_NOT_ALLOWED: 'ACTION_NOT_ALLOWED',
  SERVICE_DATE_OUT_OF_WINDOW: 'SERVICE_DATE_OUT_OF_WINDOW',
  TOO_MANY_LINES: 'TOO_MANY_LINES',
};
// These two mean the list on screen is stale: the dialog reloads it and shows this.
export const FIX_CHARGES_RELOAD_CODES = [FIX_CHARGES_ERROR_CODE.ACTION_NOT_ALLOWED, FIX_CHARGES_ERROR_CODE.CHARGES_CHANGED];
export const FIX_CHARGES_RELOAD_COPY = 'These charges were just changed by someone else. The list below is up to date. Check it and try again.';
const FIX_CHARGES_STALE_COPY = 'These charges were changed by someone else. Close this window, reopen it and try again.';
export const FIX_CHARGES_ERROR_COPY = {
  [FIX_CHARGES_ERROR_CODE.REASON_REQUIRED]: 'Choose why these charges are being fixed.',
  [FIX_CHARGES_ERROR_CODE.MOVE_NOT_ALLOWED_STATUS]:
    'The discharge has already been initiated. Close this window and reopen it to see its current state.',
  [FIX_CHARGES_ERROR_CODE.CHARGE_NOT_FOUND]: FIX_CHARGES_STALE_COPY,
  [FIX_CHARGES_ERROR_CODE.CHARGE_REVERSED]: FIX_CHARGES_STALE_COPY,
  [FIX_CHARGES_ERROR_CODE.CHARGE_NOT_AFTER_DISCHARGE_DATE]: FIX_CHARGES_STALE_COPY,
  [FIX_CHARGES_ERROR_CODE.CHARGES_CHANGED]: FIX_CHARGES_RELOAD_COPY,
  [FIX_CHARGES_ERROR_CODE.ACTION_NOT_ALLOWED]: FIX_CHARGES_RELOAD_COPY,
  [FIX_CHARGES_ERROR_CODE.SERVICE_DATE_OUT_OF_WINDOW]:
    "The discharge date is outside this patient's stay. Choose another date.",
  [FIX_CHARGES_ERROR_CODE.TOO_MANY_LINES]:
    'Too many charges to fix in one go. Ask your admin to check this bill.',
  [ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR]:
    "Only this patient's treating doctors or the front desk can discharge this patient.",
};
export const fixChargesErrorCopy = (code, role) => (
  code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE
    ? outOfDepartmentCopy(role)
    : FIX_CHARGES_ERROR_COPY[code] || 'The charges could not be fixed. Try again.'
);
